import { inject, Injectable } from '@angular/core';

import { Observable, share, switchMap, take, zip } from 'rxjs';
import { Store } from '@ngrx/store';

import { AppState } from '@fcom/core';
import { ConfigService } from '@fcom/core/services/config/config.service';
import {
  ServiceAvailability,
  ServiceAvailabilityPerFragment,
  ServiceCatalog,
  ServiceCatalogCategory,
  ServiceCatalogServices,
} from '@fcom/dapi';
import { retryWithBackoff } from '@fcom/rx';
import { combine, isPresent, mapValues } from '@fcom/core/utils';
import {
  CallerType,
  Category,
  FinnairServiceCatalogV3,
  RestError,
  SeatmapSvgAndControlData,
} from '@fcom/dapi/api/models';
import { language } from '@fcom/core/selectors/language.selector';
import { DapiService } from '@fcom/dapi/api/dapi.service';
import { getSeatmapFromCart, getSeatmapFromOrder } from '@fcom/dapi/api/ancillary-functions';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

import { ServicesActions } from '../../../store/actions';
import { seatLastToken } from '../../../store/selectors';
import { asPurchasableServiceCatalog, asServiceCatalogCategory, isAncillaryBundle } from '../utils';
import { CartOrOrder } from '../../../interfaces';

export interface SetServiceCatalogOptions {
  keepQuotaLimitedProducts?: boolean;
}

const asAvailability = <T extends ServiceCatalogServices>(services: T): ServiceAvailabilityPerFragment => {
  if (!isPresent(services)) {
    return {};
  }
  return Object.keys(services).reduce((availability: ServiceAvailabilityPerFragment, fragmentId: string) => {
    availability[fragmentId] = combine(services[fragmentId]).length > 0;
    return availability;
  }, {});
};

export const asServiceAvailability = (catalog: ServiceCatalog): ServiceAvailability => {
  const baseAvailability: ServiceAvailability = {
    [Category.CABIN_BAGGAGE]: {},
    [Category.BAGGAGE]: {},
    [Category.MEAL]: {},
    [Category.WIFI]: {},
    [Category.LOUNGE]: {},
    [Category.COVER]: {},
    [Category.SPORT]: {},
    [Category.PET]: {},
    [Category.SAF]: {},
    [Category.PRIORITY]: {},
    ...Object.fromEntries(
      Object.values(Category)
        .filter(isAncillaryBundle)
        .map((category) => [category, {}])
    ),
  };

  return mapValues(baseAvailability, (old, key: string) => {
    const category = catalog?.categories.find((c) => c.category.toString() === key) || ({} as ServiceCatalogCategory);

    return asAvailability(category?.services || {}) || old;
  });
};

const asSeatmapTheme = (theme: FinnairTheme): string => (theme === FinnairTheme.LEGACY ? 'legacy-theme' : theme);

const asErrorMessage = (error: RestError): string => {
  return `${error?.errorMessage}`.concat(
    error?.amadeusResponse ? `${error.amadeusResponse.messageTitle} (${error.amadeusResponse.messageCode})` : ''
  );
};

export const asServiceCatalog = (catalog: FinnairServiceCatalogV3): ServiceCatalog => {
  return {
    messages: catalog.messages?.map((error) => asErrorMessage(error)) ?? [],
    campaigns: catalog.campaigns ?? [],
    paxLevelQuota: catalog.paxLevelQuota,
    categories: catalog.categories.map((category) => asServiceCatalogCategory(category)),
  };
};

@Injectable()
export class CommonBookingAncillaryService {
  private dapiService = inject(DapiService);
  private configService = inject(ConfigService);
  private store$ = inject<Store<AppState>>(Store);
  private themeService = inject(FinnairThemeService);

  static NUMBER_OF_RETRIES = 1;

  setServiceCatalog(cartOrOrder: CartOrOrder, options: SetServiceCatalogOptions = {}): void {
    const responseCatalog: FinnairServiceCatalogV3 = cartOrOrder.serviceCatalog || {
      messages: [],
      categories: [],
      campaigns: [],
      paxLevelQuota: { combinedQuota: 0, variants: [] },
    };
    const serviceCatalog = asServiceCatalog(responseCatalog);
    const catalog = asPurchasableServiceCatalog(serviceCatalog, {
      keepQuotaLimitedProducts: options.keepQuotaLimitedProducts && this.configService.cfg.enableAncillaryFrontendQuota,
    });

    this.store$.dispatch(ServicesActions.setServiceCatalog({ catalog }));
    this.store$.dispatch(
      ServicesActions.setServiceAvailability({ serviceAvailability: asServiceAvailability(catalog) })
    );
  }

  getCartSeatMapForFlight(
    flightId: string,
    departureDateTime: string,
    cartId: string,
    cartHash: string,
    currencyCode?: string
  ): Observable<SeatmapSvgAndControlData> {
    return this.store$.pipe(
      language(),
      take(1),
      switchMap((locale) => {
        return this.dapiService
          .invoke$(getSeatmapFromCart, this.configService.cfg.servicesUrl, {
            flightId,
            departureDateTime,
            cartId,
            hash: cartHash,
            version: 2,
            locale,
            theme: asSeatmapTheme(this.themeService.siteTheme()),
            Caller: CallerType.FINNAIRCOM,
            ...(currencyCode ? { currencyCode } : {}),
          })
          .pipe(retryWithBackoff(CommonBookingAncillaryService.NUMBER_OF_RETRIES), share());
      })
    );
  }

  getOrderSeatMapForFlight(
    flightId: string,
    departureDateTime: string,
    orderId: string,
    orderHash: string,
    currencyCode?: string
  ): Observable<SeatmapSvgAndControlData> {
    return zip(this.store$.pipe(language()), this.store$.pipe(seatLastToken())).pipe(
      take(1),
      switchMap(([locale, token]) => {
        return this.dapiService
          .invoke$(getSeatmapFromOrder, this.configService.cfg.servicesUrl, {
            flightId,
            departureDateTime,
            orderId,
            hash: orderHash,
            version: 2,
            locale,
            token,
            theme: asSeatmapTheme(this.themeService.siteTheme()),
            Caller: CallerType.FINNAIRCOM,
            ...(currencyCode ? { currencyCode } : {}),
          })
          .pipe(retryWithBackoff(CommonBookingAncillaryService.NUMBER_OF_RETRIES), share());
      })
    );
  }
}
